/**
 * Local Delivery - fee product cart add/remove (express + standard tiers)
 */
(function () {
  'use strict';

  function vid(id) { return Number(id) || 0; }

  // BUG-107: every fee mutation runs through one queue so a checkout-time
  // re-assert can never interleave with an in-flight slot-select add —
  // parallel /cart/add.js calls for the same variant merge to quantity 2
  // (a double charge), and add/remove races can drop the fee entirely.
  var feeOpChain = Promise.resolve();
  function enqueueFeeOp(fn) {
    var next = feeOpChain.then(fn, fn);
    feeOpChain = next.then(function () {}, function () {});
    return next;
  }

  function cartFetch() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function isFeeItem(item) {
    if (!item || typeof item !== 'object') return false;
    if (!item.properties || typeof item.properties !== 'object') return false;
    var isNewFee = String(item.properties._delivery_fee || '').toLowerCase() === 'true';
    var isLegacyExpress = String(item.properties._express || '').toLowerCase() === 'true';
    return isNewFee || isLegacyExpress;
  }

  function listFeeItems(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.filter(isFeeItem);
  }

  function findFeeInCart(cart, variantId) {
    var targetVariantId = vid(variantId);
    return listFeeItems(cart).find(function (item) {
      return vid(item.variant_id) === targetVariantId;
    }) || null;
  }

  function buildFeeProperties(input) {
    var mode = String((input && input.mode) || '').toLowerCase();
    var timeslot = String((input && input.timeslot) || '');
    var label = String((input && input.label) || '');
    var props = {
      _delivery_fee: 'true',
      _delivery_fee_mode: mode || '',
    };
    if (timeslot) props._delivery_timeslot = timeslot;
    if (label) props._delivery_fee_label = label;
    if (mode === 'express') props._express = 'true';
    return props;
  }

  function changeLineItem(itemKey, quantity, properties) {
    var payload = { id: itemKey, quantity: quantity };
    if (properties) payload.properties = properties;
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function removeFeeItemsExcept(cart, keepVariantId) {
    var keep = vid(keepVariantId);
    var feeItems = listFeeItems(cart).filter(function (item) {
      return keep === 0 || vid(item.variant_id) !== keep;
    });
    if (feeItems.length === 0) return Promise.resolve({ changed: false });

    var chain = Promise.resolve();
    feeItems.forEach(function (item) {
      chain = chain.then(function () { return changeLineItem(item.key, 0); });
    });
    return chain.then(function () { return { changed: true }; });
  }

  function removeAllFeeProducts() {
    return enqueueFeeOp(function () {
      return cartFetch().then(function (cart) {
        return removeFeeItemsExcept(cart, null);
      });
    });
  }

  function verifyFeeInCart(input) {
    return cartFetch().then(function (cart) {
      var feeItem = findFeeInCart(cart, input.variantId);
      if (!feeItem) return { present: false, cart: cart };
      var expectedSlot = String(input.timeslot || '');
      var expectedMode = String(input.mode || '').toLowerCase();
      var currentSlot = feeItem.properties ? String(feeItem.properties._delivery_timeslot || '') : '';
      var currentMode = feeItem.properties ? String(feeItem.properties._delivery_fee_mode || '').toLowerCase() : '';
      var slotMatches = !expectedSlot || currentSlot === expectedSlot;
      var modeMatches = !expectedMode || currentMode === expectedMode;
      return {
        present: slotMatches && modeMatches,
        item: feeItem,
        cart: cart,
      };
    });
  }

  function updateFeePropertiesNow(input) {
    return cartFetch().then(function (cart) {
      var item = findFeeInCart(cart, input.variantId);
      if (!item) return null;
      return changeLineItem(item.key, 1, buildFeeProperties(input));
    });
  }

  function updateFeeProperties(input) {
    return enqueueFeeOp(function () { return updateFeePropertiesNow(input); });
  }

  function addFeeProductNow(input) {
    var variantId = String(input.variantId || '').trim();
    if (!variantId) return Promise.resolve({ changed: false, present: false });

    return cartFetch()
      .then(function (cart) {
        return removeFeeItemsExcept(cart, variantId).then(function () { return cartFetch(); });
      })
      .then(function (refreshed) {
        var existing = findFeeInCart(refreshed || null, variantId);
        var expectedSlot = String(input.timeslot || '');
        var expectedMode = String(input.mode || '').toLowerCase();
        if (existing) {
          var currentSlot = existing.properties ? String(existing.properties._delivery_timeslot || '') : '';
          var currentMode = existing.properties ? String(existing.properties._delivery_fee_mode || '').toLowerCase() : '';
          if (existing.quantity === 1 && currentSlot === expectedSlot && currentMode === expectedMode) {
            return { changed: false, item: existing, present: true, cart: refreshed };
          }
          // Stale slot/mode, or quantity !== 1 (merged parallel adds) — normalize
          // to exactly one fee line carrying the current properties.
          return changeLineItem(existing.key, 1, buildFeeProperties(input)).then(function () {
            return verifyFeeInCart(input).then(function (verified) {
              return { changed: true, present: verified.present, item: verified.item, cart: verified.cart };
            });
          });
        }

        var numericVariantId = vid(variantId);
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: numericVariantId,
            quantity: 1,
            properties: buildFeeProperties(input),
          }),
          credentials: 'same-origin',
        }).then(function (r) {
          if (!r.ok) {
            return r.text().then(function (text) {
              console.error('[wf-ld-fee] /cart/add.js failed:', r.status, text);
              if (window.WF_LD_API && typeof window.WF_LD_API.reportEvent === 'function') {
                window.WF_LD_API.reportEvent('cart_add_failed', {
                  variantId: variantId,
                  statusCode: r.status,
                  detail: text,
                });
              }
              return { changed: false, present: false };
            });
          }
          return r.json().then(function (data) {
            return verifyFeeInCart(input).then(function (verified) {
              return { changed: true, item: data, present: verified.present, cart: verified.cart };
            });
          });
        }).catch(function (err) {
          console.error('[wf-ld-fee] add fee error', err);
          return { changed: false, present: false };
        });
      });
  }

  function addFeeProduct(input) {
    return enqueueFeeOp(function () { return addFeeProductNow(input); });
  }

  function removeFeeProductNow(variantId) {
    if (!variantId) return Promise.resolve({ changed: false });
    return cartFetch().then(function (cart) {
      var item = findFeeInCart(cart, variantId);
      if (!item) return { changed: false };
      return changeLineItem(item.key, 0)
        .then(function (nextCart) { return { changed: true, cart: nextCart }; })
        .catch(function () { return { changed: false }; });
    });
  }

  function removeFeeProduct(variantId) {
    return enqueueFeeOp(function () { return removeFeeProductNow(variantId); });
  }

  function refreshCartTotals() {
    return cartFetch().then(function (cart) {
      if (!cart) return;
      var digitalCardTotal = (cart.items || []).reduce(function (total, item) {
        var properties = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
        if (String(properties._wf_digital_card_fee || '').toLowerCase() !== 'yes') return total;
        var linePrice = Number(item.final_line_price != null ? item.final_line_price : item.line_price);
        return total + (isFinite(linePrice) ? linePrice : 0);
      }, 0);
      var digitalCardRow = document.getElementById('wf-digital-card-charge-row');
      var digitalCardAmount = document.getElementById('wf-digital-card-charge-amount');
      if (digitalCardRow) {
        digitalCardRow.style.display = digitalCardTotal > 0 ? 'flex' : 'none';
        if (digitalCardTotal > 0) digitalCardRow.setAttribute('data-wf-digital-card-fee', 'true');
        else digitalCardRow.removeAttribute('data-wf-digital-card-fee');
      }
      if (digitalCardAmount) digitalCardAmount.textContent = '$' + (digitalCardTotal / 100).toFixed(2);
      var formatted = '$' + (cart.total_price / 100).toFixed(2);
      var totalTargets = document.querySelectorAll('.cart__subtotal .money, .cart__subtotal-container .money, #sg-custom-subtotal-price');
      if (totalTargets && totalTargets.length > 0) {
        totalTargets.forEach(function (el) { el.textContent = formatted; });
      }
      // Eurus (revamp theme) cart total — patched in place so the delivery form is not re-rendered.
      // Preserve the rendered currency suffix (e.g. " SGD") and use thousands separators.
      var eurusTotal = document.getElementById('wf-cart-total-amount');
      if (eurusTotal) {
        var suffixMatch = (eurusTotal.textContent || '').match(/[^0-9.,\s$]+\s*$/);
        var suffix = suffixMatch ? (' ' + suffixMatch[0].trim()) : '';
        var amountStr = (cart.total_price / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        eurusTotal.textContent = '$' + amountStr + suffix;
      }
      if (window.PXUTheme && window.PXUTheme.jsCart && typeof window.PXUTheme.jsCart.updateView === 'function') {
        try { window.PXUTheme.jsCart.updateView(cart, null); } catch (_) {}
      }
      if (window.PXUTheme && window.PXUTheme.jsAjaxCart && typeof window.PXUTheme.jsAjaxCart.updateView === 'function') {
        try { window.PXUTheme.jsAjaxCart.updateView(); } catch (_) {}
      }
    });
  }

  function setFeeChargeVisible(show) {
    var row = document.getElementById('wf-express-charge-row');
    if (row) row.style.display = show ? 'flex' : 'none';
  }

  function setFeeCharge(label, amount) {
    var labelEl = document.getElementById('wf-delivery-fee-label');
    var amountEl = document.getElementById('wf-delivery-fee-amount');
    if (labelEl) labelEl.textContent = label || 'Delivery Fee';
    if (amountEl) {
      var numericAmount = Number(amount);
      amountEl.textContent = isFinite(numericAmount) ? ('$' + numericAmount.toFixed(2)) : '$0.00';
    }
  }

  // Backward-compatible alias API (existing local-delivery-cart.js calls).
  function addExpressProduct(variantId, timeslot) {
    return addFeeProduct({
      variantId: variantId,
      timeslot: timeslot,
      mode: 'express',
      label: 'Express Delivery',
    });
  }

  function removeExpressProduct(variantId) {
    return removeFeeProduct(variantId);
  }

  function updateExpressProperties(variantId, timeslot) {
    return updateFeeProperties({
      variantId: variantId,
      timeslot: timeslot,
      mode: 'express',
      label: 'Express Delivery',
    });
  }

  window.WF_LD_EXPRESS_CART = {
    addFeeProduct: addFeeProduct,
    removeFeeProduct: removeFeeProduct,
    removeAllFeeProducts: removeAllFeeProducts,
    updateFeeProperties: updateFeeProperties,
    refreshCartTotals: refreshCartTotals,
    setFeeChargeVisible: setFeeChargeVisible,
    setFeeCharge: setFeeCharge,
    addExpressProduct: addExpressProduct,
    removeExpressProduct: removeExpressProduct,
    updateExpressProperties: updateExpressProperties,
    removeAllExpressProducts: removeAllFeeProducts,
    setExpressChargeVisible: setFeeChargeVisible,
  };
})();
